import { createFileRoute, useRouter } from '@tanstack/react-router'
import {
  DocumentAgentType,
  listDocumentAgentArtifacts,
  listDocumentAgentTypes
} from '@/api/documentAgent/documentAgent'
import type { DocumentAgentType as DocumentAgentTypeValue } from '@/api/documentAgent/documentAgent'
import { useProjectBreadcrumb } from '@/common/useProjectBreadcrumb'
import { DocumentAgentStart } from '@/components/documentAgent/DocumentAgentStart'
import { PageHeader } from '@/components/PageHeader'

const newDocumentRouteByType = {
  [DocumentAgentType.Proposal]: '/$orgSlug/$projectId/proposal-agent',
  [DocumentAgentType.Presentation]: '/$orgSlug/$projectId/presentation-agent'
} as const satisfies Record<DocumentAgentTypeValue, string>

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/document-agent/')({
  loader: async ({ params }) => {
    const projectId = parseInt(params.projectId, 10)
    const [{ types }, { artifacts }] = await Promise.all([
      listDocumentAgentTypes(projectId),
      listDocumentAgentArtifacts(projectId)
    ])
    return { types, artifacts }
  },
  head: () => ({ meta: [{ title: 'Document Agent - Snowvault' }] }),
  component: DocumentAgentIndex
})

function DocumentAgentIndex() {
  const { orgSlug, projectId } = Route.useParams()
  const { types, artifacts } = Route.useLoaderData()
  const { projectTitle, projectTo } = useProjectBreadcrumb()
  const router = useRouter()

  function selectType(type: DocumentAgentTypeValue) {
    void router.navigate({
      to: newDocumentRouteByType[type],
      params: { orgSlug, projectId }
    })
  }

  function selectArtifact(type: DocumentAgentTypeValue, artifactId: number) {
    if (type === DocumentAgentType.Proposal) {
      void router.navigate({
        to: '/$orgSlug/$projectId/proposal-agent/$proposalId',
        params: { orgSlug, projectId, proposalId: String(artifactId) }
      })
      return
    }
    void router.navigate({
      to: '/$orgSlug/$projectId/presentation-agent/$presentationId',
      params: { orgSlug, projectId, presentationId: String(artifactId) }
    })
  }

  return (
    <>
      <PageHeader breadcrumbs={[{ label: projectTitle, to: projectTo }, { label: 'Document Agent' }]} />
      <DocumentAgentStart
        types={types}
        artifacts={artifacts}
        onSelectType={selectType}
        onSelectArtifact={selectArtifact}
      />
    </>
  )
}
